import { createLogPrefix, type LogTarget } from '@amazon/vinyl-util'
import { createErrorHandler, handleError } from '@/errorHandler'
import { shouldForceErrorFor } from '@/util/forceError'

export interface Context {
    readonly target: LogTarget
    readonly errorHandler: (error: Error) => void
    /**
     * Log the error and broadcast it on the error signal for diagnostics
     * capture. Use where the surface already renders its own contextual
     * load-error (inline retry row, not-found shell). Prefer `errorHandler`
     * everywhere else.
     */
    readonly reportError: (error: unknown) => void
}

// Track the components we've already fired a synthetic error for this
// session — `ctx('Feed')` is called eagerly at module-import time from
// several sites; without the guard a `?force-error=Feed` URL would
// re-throw on every subsequent `ctx()` call.
const forcedComponentsFired = new Set<string>()

/**
 * Creates a logging target and error handler with the given name.
 *
 * When `?force-error=<name>` matches, schedule a synthetic error on the
 * next tick — routed through the same handler as any real error would
 * be. Used by the Debug Controls page (`/debug`) to exercise the
 * platform error surface without editing product code.
 * @param name
 */
export function ctx(name: string): Context {
    const target: LogTarget = { logPrefix: createLogPrefix(name) }
    const errorHandler = createErrorHandler(target)
    if (shouldForceErrorFor(name) && !forcedComponentsFired.has(name)) {
        forcedComponentsFired.add(name)
        setTimeout(() => {
            errorHandler(new Error(`force-error: synthetic failure in ${name}`))
        }, 0)
    }
    return {
        target,
        errorHandler,
        reportError: (error: unknown) => handleError(target, error),
    }
}
